import React from 'react';

const printStyles = `
  @page { size: A4; margin: 6mm; }
  @media print {
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
    .pharma-invoice { box-shadow: none !important; margin: 0 auto !important; width: 210mm !important; border: none !important; }
  }
`;

const fmt = (v: any) => {
  const n = typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : Number(v || 0);
  if (isNaN(n)) return '';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const numberToWords = (n: number): string => {
  if (!n || n === 0) return 'Zero';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (x: number): string => {
    if (x === 0) return '';
    if (x < 20) return ones[x] + ' ';
    if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '') + ' ';
    if (x < 1000) return ones[Math.floor(x / 100)] + ' Hundred ' + toW(x % 100);
    if (x < 100000) return toW(Math.floor(x / 1000)) + 'Thousand ' + toW(x % 1000);
    if (x < 10000000) return toW(Math.floor(x / 100000)) + 'Lakh ' + toW(x % 100000);
    return toW(Math.floor(x / 10000000)) + 'Crore ' + toW(x % 10000000);
  };
  return 'Rs. ' + toW(Math.floor(Math.abs(n))).trim() + ' only';
};

const b = '1px solid #000';
const cellStyle = (w: string, align = 'left', bold = false): React.CSSProperties => ({
  width: w, padding: '3px 5px', borderRight: b, textAlign: align as any,
  fontWeight: bold ? 700 : 400, fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden',
});

const PharmacyInvoice = ({ data }: { data: any }) => {
  if (!data) return null;

  const company = data.company || {};
  const meta = data.meta || {};
  const items = data.items || [];
  const totals = data.totals || {};
  const patient = data.patient || {};
  const doctor = data.doctor || {};

  const subtotal = items.reduce((s: number, it: any) => s + (parseFloat(it.amount) || 0), 0);
  const discount = parseFloat(totals.discount) || 0;
  const gstRows = totals.gstRows || [];
  const grandTotal = parseFloat(totals.amount) || subtotal - discount + gstRows.reduce((s: number, g: any) => s + (g.amount || 0), 0);

  return (
    <>
      <style>{printStyles}</style>
      <div className="pharma-invoice mx-auto bg-white"
        style={{ width: '210mm', boxSizing: 'border-box', fontSize: '12px', lineHeight: 1.3, padding: '4px', fontFamily: 'Arial, sans-serif', color: '#000' }}>
        <div style={{ border: b, display: 'flex', flexDirection: 'column' }}>

          {/* Company Header */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '8px 10px' }}>
            <div style={{ fontWeight: 900, fontSize: '20px', letterSpacing: '2px', textTransform: 'uppercase' }}>{company.name || ''}</div>
            {company.address && <div style={{ fontSize: '11px', marginTop: '2px' }}>{Array.isArray(company.address) ? company.address.join(', ') : company.address}</div>}
            <div style={{ fontSize: '10px', marginTop: '3px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {company.phone && <span>Phone: {company.phone}</span>}
              {company.email && <span>E-Mail: {company.email}</span>}
            </div>
            <div style={{ fontSize: '10px', marginTop: '2px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {company.gstin && <span>GSTIN: <strong>{company.gstin}</strong></span>}
              {(company as any).foodLicense && <span>FOOD LIC.: {(company as any).foodLicense}</span>}
            </div>
          </div>

          {/* GST INVOICE Title */}
          <div style={{ borderBottom: b, textAlign: 'center', padding: '4px', fontWeight: 900, fontSize: '16px', letterSpacing: '3px' }}>
            GST INVOICE
          </div>

          {/* Patient + Invoice Details Row */}
          <div style={{ display: 'flex', borderBottom: b }}>
            {/* Left: Patient / Doctor */}
            <div style={{ width: '55%', borderRight: b, padding: '6px 10px', fontSize: '11px' }}>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                <span style={{ fontWeight: 700, width: '90px', flexShrink: 0 }}>Patient Name :</span>
                <span>{patient.name || ''}</span>
              </div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                <span style={{ fontWeight: 700, width: '90px', flexShrink: 0 }}>Patient Address :</span>
                <span>{patient.address || ''}</span>
              </div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                <span style={{ fontWeight: 700, width: '90px', flexShrink: 0 }}>Dr Name :</span>
                <span>{doctor.name || ''}</span>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <span style={{ fontWeight: 700, width: '90px', flexShrink: 0 }}>Dr Reg No :</span>
                <span>{doctor.regNo || ''}</span>
              </div>
            </div>
            {/* Right: Invoice No / Date */}
            <div style={{ width: '45%', padding: '6px 10px', fontSize: '11px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                <span style={{ fontWeight: 700 }}>Invoice No. :</span>
                <span style={{ fontWeight: 700 }}>{meta.invoiceNo || ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700 }}>Date :</span>
                <span style={{ fontWeight: 700 }}>{meta.date || ''}</span>
              </div>
            </div>
          </div>

          {/* Items Header */}
          <div style={{ display: 'flex', borderBottom: b, backgroundColor: '#f5f5f5', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            <div style={cellStyle('4%', 'center', true)}>SN.</div>
            <div style={{ ...cellStyle('20%', 'left', true) }}>Product Name</div>
            <div style={cellStyle('6%', 'center', true)}>Pack</div>
            <div style={cellStyle('6%', 'center', true)}>HSN</div>
            <div style={cellStyle('10%', 'center', true)}>Batch</div>
            <div style={cellStyle('6%', 'center', true)}>Exp.</div>
            <div style={cellStyle('5%', 'center', true)}>Qty</div>
            <div style={cellStyle('9%', 'right', true)}>MRP</div>
            <div style={cellStyle('9%', 'right', true)}>Rate</div>
            <div style={cellStyle('6%', 'right', true)}>Disc</div>
            <div style={{ ...cellStyle('9%', 'right', true), borderRight: 'none' }}>Amount</div>
          </div>

          {/* Item Rows */}
          {items.map((item: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', borderBottom: b, fontSize: '11px' }}>
              <div style={cellStyle('4%', 'center')}>{idx + 1}.</div>
              <div style={{ ...cellStyle('20%', 'left'), fontWeight: 600, fontSize: '11px' }}>{item.name || item.description || ''}</div>
              <div style={cellStyle('6%', 'center')}>{item.pack || ''}</div>
              <div style={cellStyle('6%', 'center')}>{item.hsn || ''}</div>
              <div style={cellStyle('10%', 'center')}>{item.batch || ''}</div>
              <div style={cellStyle('6%', 'center')}>{item.expiry || ''}</div>
              <div style={cellStyle('5%', 'center', true)}>{item.qty || ''}</div>
              <div style={cellStyle('9%', 'right')}>{fmt(item.mrp)}</div>
              <div style={cellStyle('9%', 'right')}>{fmt(item.rate)}</div>
              <div style={cellStyle('6%', 'right')}>{fmt(item.discount)}</div>
              <div style={{ ...cellStyle('9%', 'right', true), borderRight: 'none' }}>{fmt(item.amount)}</div>
            </div>
          ))}

          {/* Empty rows to fill space */}
          {items.length < 10 && Array.from({ length: Math.min(5, 10 - items.length) }).map((_, i) => (
            <div key={`e${i}`} style={{ display: 'flex', borderBottom: b, minHeight: '18px', fontSize: '11px' }}>
              <div style={cellStyle('4%')}>&nbsp;</div>
              <div style={cellStyle('20%')}>&nbsp;</div>
              <div style={cellStyle('6%')}>&nbsp;</div>
              <div style={cellStyle('6%')}>&nbsp;</div>
              <div style={cellStyle('10%')}>&nbsp;</div>
              <div style={cellStyle('6%')}>&nbsp;</div>
              <div style={cellStyle('5%')}>&nbsp;</div>
              <div style={cellStyle('9%')}>&nbsp;</div>
              <div style={cellStyle('9%')}>&nbsp;</div>
              <div style={cellStyle('6%')}>&nbsp;</div>
              <div style={{ ...cellStyle('9%'), borderRight: 'none' }}>&nbsp;</div>
            </div>
          ))}

          {/* Totals Section */}
          <div style={{ display: 'flex', borderBottom: b }}>
            {/* Left: Terms & GST */}
            <div style={{ width: '60%', borderRight: b, padding: '6px 10px', fontSize: '10px' }}>
              {/* GST Breakup */}
              {gstRows.length > 0 && (
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 700 }}>GST: </span>
                  {gstRows.map((g: any, i: number) => (
                    <span key={i}>{g.label}: {fmt(g.amount)}{i < gstRows.length - 1 ? ' + ' : ''}</span>
                  ))}
                </div>
              )}
              {/* Terms */}
              <div style={{ fontWeight: 700, marginBottom: '3px', fontSize: '11px' }}>Terms & Conditions</div>
              <div style={{ fontSize: '9px', lineHeight: 1.5, color: '#333' }}>
                <div>Goods once sold will not be taken back or exchanged.</div>
                <div>Bills not paid due will attract 24% interest.</div>
                <div>All disputes subject to Jurisdiction only.</div>
                <div>Prescribed Sales Tax declaration will be given.</div>
              </div>
            </div>

            {/* Right: Sub Total, Discount, Grand Total */}
            <div style={{ width: '40%', padding: '0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: b, fontWeight: 700, fontSize: '12px' }}>
                <span>SUB TOTAL</span>
                <span>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: b, fontSize: '12px' }}>
                <span style={{ fontWeight: 700 }}>DISCOUNT</span>
                <span>{fmt(discount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', fontWeight: 900, fontSize: '14px' }}>
                <span>GRAND TOTAL</span>
                <span>{fmt(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Amount in Words + Signature */}
          <div style={{ display: 'flex', minHeight: '50px' }}>
            <div style={{ width: '60%', borderRight: b, padding: '6px 10px', fontSize: '11px' }}>
              <span style={{ fontWeight: 700 }}>{numberToWords(grandTotal)}</span>
            </div>
            <div style={{ width: '40%', padding: '6px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '11px' }}>For {company.name || ''}</div>
              <div style={{ textAlign: 'right', fontSize: '10px', fontWeight: 600, borderTop: '1px solid #ccc', paddingTop: '3px', marginTop: 'auto' }}>Authorised Signatory</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default PharmacyInvoice;
